/**
 * AnalyticsDrilldown — full-screen overlay listing all transactions in a
 * category for the current period.
 *
 * Pixel-faithful port of the `AnalyticsDrilldown` function in
 * `design/project/screens-analytics.jsx`. Includes:
 *   - Total card with MoM / YoY CompareChip row
 *   - 6-month trend bars with YoY overlay
 *   - Sub-category breakdown (CatBarRow rows, tappable to filter)
 *   - Sort menu: 日期由新至舊 / 金額由高至低 / 依子分類
 *   - Active sub-category filter chips
 *   - Filter button in the header (highlighted when active)
 *   - Transaction list (filtered + sorted)
 *
 * Sharp edges:
 *   - Tap on a tx row calls `onTxClick(tx)`; parent decides what to open
 *     (typically `<TxDetail />`).
 *   - Empty list → reuses the `<EmptyAnalytics />` illustration.
 */
import { useMemo, useState, type CSSProperties } from 'react';

import Icon, { type IconName } from '@/components/common/Icon';
import { formatAmount } from '@/lib/format/currency';
import { formatYmd } from '@/lib/format/date';
import { dayPart } from '@/lib/format/datetime';
import { convertMinor } from '@/lib/format/fx';
import { displayTxName } from '@/lib/displayTxName';
import { useStore } from '@/state/store';
import type { CategoryRow, IsoDate, TransactionRow, TxKind } from '@/lib/sheets/types';
import EmptyAnalytics from './EmptyAnalytics';
import { CompareChip } from './AnalyticsScreen';
import { readDisplayTags } from '@/lib/tags';

export type SortMode = 'date-desc' | 'amt-desc' | 'sub';

export interface AnalyticsDrilldownProps {
  categoryId: string;
  /** ISO start (inclusive) / end (inclusive) of the period. */
  period: { start: IsoDate; end: IsoDate };
  /** Filter by kind (matches AnalyticsScreen mode). */
  mode: TxKind;
  currency: string;
  /** Period label for the header — same string AnalyticsScreen displays. */
  periodLabel?: string;
  onClose: () => void;
  onTxClick?: (tx: TransactionRow) => void;
}

export default function AnalyticsDrilldown({
  categoryId,
  period,
  mode,
  currency,
  periodLabel,
  onClose,
  onTxClick,
}: AnalyticsDrilldownProps): JSX.Element {
  const transactions = useStore((s) => s.transactions.items);
  const categories = useStore((s) => s.categories.items);
  const activeBookId = useStore((s) => s.books.activeBookId);

  const category: CategoryRow | undefined = useMemo(
    () => categories.find((c) => c.id === categoryId),
    [categories, categoryId],
  );

  // Sub-category list (children of this parent)
  const subCats = useMemo<CategoryRow[]>(() => {
    return categories.filter(
      (c) => c.parent_id === categoryId && !c.deleted_at,
    );
  }, [categories, categoryId]);

  const subLookup = useMemo(() => {
    const m = new Map<string, CategoryRow>();
    for (const c of subCats) m.set(c.id, c);
    return m;
  }, [subCats]);

  // All transactions (unfiltered by sub) for this cat in the period.
  // Mixed-currency: each tx is FX-converted on display/aggregation; we keep
  // the original row here so the per-row 原幣 display still works.
  const allItems = useMemo<TransactionRow[]>(() => {
    return transactions.filter((t) => {
      if (t.deleted_at) return false;
      if (t.kind !== mode) return false;
      if (t.category_id !== categoryId) return false;
      if (activeBookId && t.book_id !== activeBookId) return false;
      const day = dayPart(t.date);
      return day >= period.start && day <= period.end;
    });
  }, [transactions, mode, categoryId, period, activeBookId]);

  const [sort, setSort] = useState<SortMode>('date-desc');
  const [subFilter, setSubFilter] = useState<Set<string>>(new Set());
  const [filterOpen, setFilterOpen] = useState(false);

  const subAgg = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of allItems) {
      const k = t.subcategory_id || '__nosub__';
      const minor = convertMinor(t.amount_minor, t.currency || 'TWD', currency);
      m.set(k, (m.get(k) ?? 0) + minor);
    }
    const total = [...m.values()].reduce((a, b) => a + b, 0);
    return [...m.entries()]
      .map(([id, v]) => ({
        id,
        name: subLookup.get(id)?.name ?? '未分類',
        v,
        pct: total ? (v / total) * 100 : 0,
      }))
      .sort((a, b) => b.v - a.v);
  }, [allItems, subLookup, currency]);

  const visibleItems = useMemo<TransactionRow[]>(() => {
    let arr = allItems;
    if (subFilter.size > 0) {
      arr = arr.filter((t) => subFilter.has(t.subcategory_id || '__nosub__'));
    }
    const sorted = [...arr];
    if (sort === 'amt-desc') {
      sorted.sort((a, b) => b.amount_minor - a.amount_minor);
    } else if (sort === 'sub') {
      sorted.sort((a, b) =>
        (a.subcategory_id || '').localeCompare(b.subcategory_id || ''),
      );
    } else {
      sorted.sort((a, b) => {
        if (a.date !== b.date) return a.date < b.date ? 1 : -1;
        if (a.created_at !== b.created_at)
          return a.created_at < b.created_at ? 1 : -1;
        return 0;
      });
    }
    return sorted;
  }, [allItems, subFilter, sort]);

  const total = visibleItems.reduce(
    (s, t) => s + convertMinor(t.amount_minor, t.currency || 'TWD', currency),
    0,
  );
  const isEmpty = visibleItems.length === 0;

  // 6-month trend with YoY overlay
  const trend = useMemo(() => {
    const end = new Date(period.end + 'T00:00:00');
    const months: { label: string; v: number; yoy: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(end.getFullYear(), end.getMonth() - i, 1);
      const dPrev = new Date(d.getFullYear() - 1, d.getMonth(), 1);
      const monthStart = formatYmd(d);
      const monthEnd = formatYmd(new Date(d.getFullYear(), d.getMonth() + 1, 0));
      const yoyStart = formatYmd(dPrev);
      const yoyEnd = formatYmd(
        new Date(dPrev.getFullYear(), dPrev.getMonth() + 1, 0),
      );
      const sumIn = (s: string, e: string): number =>
        transactions
          .filter((t) => {
            if (t.deleted_at) return false;
            if (t.kind !== mode) return false;
            if (t.category_id !== categoryId) return false;
            if (activeBookId && t.book_id !== activeBookId) return false;
            const day = dayPart(t.date);
            return day >= s && day <= e;
          })
          .reduce(
            (acc, t) =>
              acc + convertMinor(t.amount_minor, t.currency || 'TWD', currency),
            0,
          );
      months.push({
        label: `${d.getMonth() + 1}月`,
        v: sumIn(monthStart, monthEnd),
        yoy: sumIn(yoyStart, yoyEnd),
      });
    }
    return months;
  }, [period.end, transactions, mode, categoryId, currency, activeBookId]);

  const trendMax = Math.max(1, ...trend.map((x) => Math.max(x.v, x.yoy)));

  // Compare chips: vs prev month and vs same month last year
  const { momPct, yoyPct } = useMemo(() => {
    const end = new Date(period.end + 'T00:00:00');
    const curStart = formatYmd(new Date(end.getFullYear(), end.getMonth(), 1));
    const curEnd = formatYmd(
      new Date(end.getFullYear(), end.getMonth() + 1, 0),
    );
    const prevStart = formatYmd(
      new Date(end.getFullYear(), end.getMonth() - 1, 1),
    );
    const prevEnd = formatYmd(
      new Date(end.getFullYear(), end.getMonth(), 0),
    );
    const yoyStart = formatYmd(
      new Date(end.getFullYear() - 1, end.getMonth(), 1),
    );
    const yoyEnd = formatYmd(
      new Date(end.getFullYear() - 1, end.getMonth() + 1, 0),
    );
    const sumIn = (s: string, e: string): number =>
      transactions
        .filter((t) => {
          if (t.deleted_at) return false;
          if (t.kind !== mode) return false;
          if (t.category_id !== categoryId) return false;
          if (activeBookId && t.book_id !== activeBookId) return false;
          const day = dayPart(t.date);
          return day >= s && day <= e;
        })
        .reduce(
          (acc, t) =>
            acc + convertMinor(t.amount_minor, t.currency || 'TWD', currency),
          0,
        );
    const cur = sumIn(curStart, curEnd);
    const prev = sumIn(prevStart, prevEnd);
    const yoy = sumIn(yoyStart, yoyEnd);
    return {
      momPct: prev > 0 ? ((cur - prev) / prev) * 100 : 0,
      yoyPct: yoy > 0 ? ((cur - yoy) / yoy) * 100 : 0,
    };
  }, [period.end, transactions, mode, categoryId, currency, activeBookId]);

  const headerColor = category ? `var(${category.color_token})` : 'var(--cat-other)';
  const name = category ? category.name : '其他';

  const hasFilter = subFilter.size > 0;

  const toggleSub = (id: string): void => {
    setSubFilter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${name} 分類明細`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 90,
        background: 'var(--bg-app)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        style={{
          paddingTop: 54,
          padding: '54px 20px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'var(--bg-app)',
          borderBottom: '0.5px solid var(--border-1)',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="返回"
          style={iconBtn()}
        >
          <Icon name="chevL" size={22} stroke="var(--fg-1)" />
        </button>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: headerColor,
              display: 'grid',
              placeItems: 'center',
            }}
            aria-hidden="true"
          >
            {category?.icon && (
              <Icon
                name={(category.icon || 'tag') as never}
                size={14}
                stroke="#fff"
              />
            )}
          </div>
          <div
            style={{
              font: '600 16px var(--font-cjk)',
              color: 'var(--fg-1)',
            }}
          >
            {name}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setFilterOpen((v) => !v)}
          aria-label="篩選子分類"
          aria-expanded={filterOpen}
          style={{
            ...iconBtn(),
            background:
              hasFilter || filterOpen ? 'var(--accent)' : 'var(--bg-raised)',
            position: 'relative',
          }}
        >
          <Icon
            name="filter"
            size={16}
            stroke={hasFilter || filterOpen ? '#fff' : 'var(--fg-2)'}
          />
          {hasFilter && (
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 6,
                right: 6,
                width: 7,
                height: 7,
                borderRadius: 4,
                background: 'var(--honey-500)',
                border: '1.5px solid var(--bg-app)',
              }}
            />
          )}
        </button>
      </div>

      {/* Body */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '16px 20px 40px',
        }}
      >
        {/* Total + compares */}
        <div
          style={{
            background: 'var(--bg-surface)',
            borderRadius: 16,
            padding: '14px 16px',
            boxShadow: 'var(--shadow-1)',
          }}
        >
          {periodLabel && (
            <div
              style={{
                font: '500 11px var(--font-cjk)',
                color: 'var(--fg-3)',
                letterSpacing: '0.06em',
              }}
            >
              {periodLabel} · {mode === 'expense' ? '支出' : '收入'}
            </div>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
              marginTop: 2,
            }}
          >
            <span
              style={{
                font: '600 30px var(--font-num)',
                fontVariantNumeric: 'tabular-nums',
                letterSpacing: '-0.02em',
                color: 'var(--fg-1)',
              }}
            >
              {formatAmount(total, currency)}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
            <CompareChip
              label="vs 上月"
              pct={momPct}
              mode={mode === 'income' ? 'income' : 'expense'}
            />
            <CompareChip
              label="vs 去年"
              pct={yoyPct}
              mode={mode === 'income' ? 'income' : 'expense'}
            />
          </div>

          {/* 6-month trend */}
          <div style={{ marginTop: 16 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                font: '500 11px var(--font-cjk)',
                color: 'var(--fg-3)',
                marginBottom: 10,
              }}
            >
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: headerColor,
                  }}
                />
                今年
              </span>
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: 'var(--sand-300)',
                  }}
                />
                去年同期
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 8,
                height: 100,
              }}
              aria-label="近 6 個月趨勢圖"
              role="img"
            >
              {trend.map((m, i) => {
                const isCurrent = i === trend.length - 1;
                return (
                  <div
                    key={i}
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      gap: 4,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        gap: 2,
                        alignItems: 'flex-end',
                        height: 76,
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          height: `${(m.yoy / trendMax) * 100}%`,
                          background: 'var(--sand-300)',
                          borderRadius: '4px 4px 0 0',
                          minHeight: 3,
                        }}
                      />
                      <div
                        style={{
                          flex: 1,
                          height: `${(m.v / trendMax) * 100}%`,
                          background: isCurrent
                            ? headerColor
                            : 'var(--cream-200)',
                          borderRadius: '4px 4px 0 0',
                          minHeight: 3,
                        }}
                      />
                    </div>
                    <div
                      style={{
                        font: '500 10px var(--font-cjk)',
                        color: isCurrent ? 'var(--fg-1)' : 'var(--fg-3)',
                        textAlign: 'center',
                      }}
                    >
                      {m.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Sub-category breakdown */}
        {subAgg.length > 1 && (
          <div style={{ marginTop: 18 }}>
            <div
              style={{
                font: '600 13px var(--font-cjk)',
                color: 'var(--fg-1)',
                padding: '0 2px 4px',
              }}
            >
              子分類佔比
            </div>
            <div
              style={{
                background: 'var(--bg-surface)',
                borderRadius: 14,
                padding: '4px 14px',
                boxShadow: 'var(--shadow-1)',
              }}
            >
              {subAgg.map((s, i) => {
                const isFiltered = subFilter.has(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => toggleSub(s.id)}
                    aria-pressed={isFiltered}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      border: 0,
                      background: 'transparent',
                      padding: '10px 0',
                      cursor: 'pointer',
                      display: 'block',
                      borderBottom:
                        i < subAgg.length - 1
                          ? '0.5px solid var(--border-1)'
                          : '0',
                      font: 'inherit',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        marginBottom: 6,
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          background: headerColor,
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          flex: 1,
                          font: '500 14px var(--font-cjk)',
                          color: 'var(--fg-1)',
                        }}
                      >
                        {s.name}
                      </span>
                      <span
                        style={{
                          font: '500 13px var(--font-num)',
                          fontVariantNumeric: 'tabular-nums',
                          color: 'var(--fg-2)',
                        }}
                      >
                        {formatAmount(s.v, currency)}
                      </span>
                      <span
                        style={{
                          minWidth: 42,
                          textAlign: 'right',
                          font: '600 13px var(--font-num)',
                          fontVariantNumeric: 'tabular-nums',
                          color: 'var(--fg-1)',
                        }}
                      >
                        {s.pct.toFixed(0)}%
                      </span>
                      {isFiltered && (
                        <Icon name="check" size={14} stroke="var(--accent)" />
                      )}
                    </div>
                    <div
                      style={{
                        height: 6,
                        borderRadius: 3,
                        background: 'var(--cream-200)',
                        overflow: 'hidden',
                        marginLeft: 38,
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.max(2, s.pct)}%`,
                          height: '100%',
                          background: headerColor,
                          borderRadius: 3,
                        }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Filter / sort bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 18,
            padding: '0 2px 8px',
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              font: '600 13px var(--font-cjk)',
              color: 'var(--fg-1)',
              marginRight: 'auto',
            }}
          >
            紀錄明細 · {visibleItems.length} 筆
          </div>
          <SortButton value={sort} onChange={setSort} />
        </div>

        {/* Active filter chips */}
        {hasFilter && (
          <div
            style={{
              display: 'flex',
              gap: 6,
              flexWrap: 'wrap',
              marginBottom: 8,
            }}
          >
            {[...subFilter].map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => toggleSub(id)}
                aria-label={`移除${subLookup.get(id)?.name ?? id}篩選`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '4px 10px',
                  background: headerColor,
                  color: '#fff',
                  border: 0,
                  borderRadius: 999,
                  font: '500 11.5px var(--font-cjk)',
                  cursor: 'pointer',
                }}
              >
                {subLookup.get(id)?.name ?? '未分類'}
                <Icon name="x" size={11} stroke="#fff" />
              </button>
            ))}
            <button
              type="button"
              onClick={() => setSubFilter(new Set())}
              style={{
                padding: '4px 10px',
                background: 'transparent',
                border: '1px solid var(--border-2)',
                borderRadius: 999,
                font: '500 11.5px var(--font-cjk)',
                color: 'var(--fg-2)',
                cursor: 'pointer',
              }}
            >
              清除
            </button>
          </div>
        )}

        {/* List */}
        {isEmpty ? (
          <div style={{ marginTop: 8 }}>
            <EmptyAnalytics caption="沒有符合條件的紀錄" />
          </div>
        ) : (
          <div
            style={{
              background: 'var(--bg-surface)',
              borderRadius: 14,
              padding: '4px 14px',
              boxShadow: 'var(--shadow-1)',
            }}
          >
            {visibleItems.map((tx, i) => (
              <button
                key={tx.id}
                type="button"
                onClick={() => onTxClick?.(tx)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 0',
                  background: 'transparent',
                  border: 0,
                  cursor: onTxClick ? 'pointer' : 'default',
                  borderBottom:
                    i < visibleItems.length - 1
                      ? '0.5px solid var(--border-1)'
                      : '0',
                  textAlign: 'left',
                  font: 'inherit',
                }}
              >
                {(() => {
                  // Prefer the sub-category icon when the tx has one — that
                  // matches the icon shown next to the sub-category name in
                  // 子分類佔比 above. Fall back to the parent category's icon.
                  const rowSub = tx.subcategory_id ? subLookup.get(tx.subcategory_id) : undefined;
                  const rowIcon = ((rowSub?.icon || category?.icon || 'tag') as IconName);
                  const rowColor = rowSub
                    ? `var(${rowSub.color_token})`
                    : headerColor;
                  return (
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background: rowColor,
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                      }}
                      aria-hidden="true"
                    >
                      <Icon name={rowIcon} size={18} stroke="#fff" />
                    </div>
                  );
                })()}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      font: '500 14px var(--font-cjk)',
                      color: 'var(--fg-1)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {displayTxName(tx, category, tx.subcategory_id ? subLookup.get(tx.subcategory_id) : undefined)}
                  </div>
                  <div
                    style={{
                      font: '400 11px var(--font-cjk)',
                      color: 'var(--fg-3)',
                      marginTop: 2,
                    }}
                  >
                    {formatYmd(tx.date)}
                    {tx.subcategory_id && subLookup.has(tx.subcategory_id)
                      ? ` · ${subLookup.get(tx.subcategory_id)!.name}`
                      : ''}
                    {(() => {
                      const dt = readDisplayTags(tx);
                      return dt.length > 0 ? ` · ${dt.join(' ')}` : '';
                    })()}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div
                    style={{
                      font: '600 14px var(--font-num)',
                      fontVariantNumeric: 'tabular-nums',
                      color:
                        tx.kind === 'income'
                          ? 'var(--matcha-700)'
                          : 'var(--fg-1)',
                    }}
                  >
                    {/* Convert to the primary display currency so the row
                     * matches the breakdown bars + total above (which are all
                     * FX-converted). Showing the raw foreign amount here made
                     * the 紀錄明細 rows look un-converted next to NT$ totals. */}
                    {formatAmount(
                      convertMinor(tx.amount_minor, tx.currency || 'TWD', currency),
                      currency,
                    )}
                  </div>
                  {tx.currency &&
                    tx.currency.toUpperCase() !== currency.toUpperCase() && (
                      <div
                        data-testid="drilldown-original-currency"
                        style={{
                          font: '400 11px var(--font-num)',
                          fontVariantNumeric: 'tabular-nums',
                          color: 'var(--fg-3)',
                          marginTop: 2,
                        }}
                      >
                        原幣 {formatAmount(tx.amount_minor, tx.currency)}
                      </div>
                    )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function iconBtn(): CSSProperties {
  return {
    width: 36,
    height: 36,
    borderRadius: 18,
    background: 'transparent',
    border: 0,
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
  };
}

interface SortButtonProps {
  value: SortMode;
  onChange: (v: SortMode) => void;
}

function SortButton({ value, onChange }: SortButtonProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const opts: { k: SortMode; label: string }[] = [
    { k: 'date-desc', label: '日期由新至舊' },
    { k: 'amt-desc', label: '金額由高至低' },
    { k: 'sub', label: '依子分類' },
  ];
  const cur = opts.find((o) => o.k === value)!;
  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="排序"
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          padding: '5px 10px',
          background: 'var(--bg-raised)',
          border: 0,
          borderRadius: 999,
          font: '500 12px var(--font-cjk)',
          color: 'var(--fg-2)',
          cursor: 'pointer',
        }}
      >
        {cur.label}
        <Icon name="chevD" size={12} stroke="var(--fg-3)" />
      </button>
      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 1 }}
          />
          <div
            role="menu"
            style={{
              position: 'absolute',
              right: 0,
              top: 'calc(100% + 4px)',
              background: 'var(--bg-surface)',
              borderRadius: 12,
              boxShadow: 'var(--shadow-3, 0 10px 24px rgba(0,0,0,0.16))',
              zIndex: 2,
              padding: 4,
              minWidth: 140,
            }}
          >
            {opts.map((o) => (
              <button
                key={o.k}
                type="button"
                role="menuitem"
                onClick={() => {
                  onChange(o.k);
                  setOpen(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: 0,
                  background:
                    value === o.k ? 'var(--bg-raised)' : 'transparent',
                  font: `${value === o.k ? 600 : 500} 13px var(--font-cjk)`,
                  color: 'var(--fg-1)',
                  cursor: 'pointer',
                }}
              >
                {o.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
